// @flow
import * as React from 'react';
import { isDefined } from 'utils/functional';
import { sprintf } from 'sprintf-js';
import scrollIntoView from 'utils/scrollIntoView';
import Box from 'components/Box';
import { H4, Body, BodySmall } from 'components/Text';
import { PrimaryButtonLink, LinkButton } from 'components/Button';
import { colors } from '../theme';

const getMessage = (resultType, query, searchLocation, useLocation) => {
  if (query) {
    if (
      searchLocation &&
      searchLocation.text &&
      (!isDefined(useLocation) || useLocation)
    ) {
      return sprintf(
        /* TRANSLATORS: search: no results message */ getText(
          '"%(query)s" near %(searchLocationText)s'
        ),
        { resultType, query, searchLocationText: searchLocation.text }
      );
    }
    return sprintf(
      /* TRANSLATORS: search: no results message */ getText('"%(query)s"'),
      { resultType, query }
    );
  }
  if (
    searchLocation &&
    searchLocation.text &&
    (!isDefined(useLocation) || useLocation)
  ) {
    return sprintf(
      /* TRANSLATORS: search: no results message */ getText(
        'All %(resultType)s near %(searchLocationText)s'
      ),
      { resultType, searchLocationText: searchLocation.text }
    );
  }
  return sprintf(
    /* TRANSLATORS: search: no results message */ getText('All %(resultType)s'),
    { resultType }
  );
};

export default ({
  query,
  noMessage,
  searchLocation,
  useLocation,
  resultType,
  clearAllFilters,
  clearSearch,
  additionalMessage
}: {
  query?: string,
  noMessage?: boolean,
  searchLocation?: SearchLocation,
  useLocation?: boolean,
  resultType: string,
  clearAllFilters?: Function,
  clearSearch?: Function,
  additionalMessage?: React.Node
}) => {
  const clearAll = () => {
    scrollIntoView();
    if (clearAllFilters) {
      clearAllFilters();
    }
    if (clearSearch) {
      clearSearch();
    }
  };

  const message = noMessage
    ? null
    : getMessage(resultType, query, searchLocation, useLocation);

  return (
    <Box>
      <Box
        display={['block', null, null, 'flex']}
        justifyContent="space-between"
      >
        <Box flex="1 1 auto" pr={16}>
          <H4 mb={0}>
            {sprintf(getText('No %(resultType)s match your search'), {
              resultType
            })}
            {message && ': '}
            {message}
          </H4>
          {additionalMessage && <Body mt={24}>{additionalMessage}</Body>}
        </Box>
        {(clearAllFilters || clearSearch) && (
          <Box flex="0 0 auto" lineHeight={1.75}>
            <LinkButton onClick={clearAll}>
              {/* TRANSLATORS: search: no results tips */ clearAllFilters
                ? getText('Clear all filters')
                : getText('Clear search')}
            </LinkButton>
          </Box>
        )}
      </Box>
      <Box
        mt={48}
        pt={32}
        pb={48}
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        width="100%"
        borderRadius={24}
        bg={colors.aluminum}
        textAlign="center"
      >
        <BodySmall>
          {getText('Looking for additional help?')}
          <br />
          {getText('Visit our Help Center for more info.')}
        </BodySmall>
        <PrimaryButtonLink
          mt={24}
          width={172}
          target="_blank"
          rel="noopener noreferrer"
          href={getText(
            /* TRANSLATORS: search: help center link url */ 'https://www.idealist.org/en/help-center/'
          )}
        >
          {/* TRANSLATORS: search: help center link text */ getText(
            'Help Center'
          )}
        </PrimaryButtonLink>
      </Box>
    </Box>
  );
};
